import React, { useState } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { FieldArray, FormikProvider, getIn, useFormik } from "formik";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "components/ui";
import {
  CustomSelect,
  SubmitLoaderButton,
  VisiblitySwitch,
} from "components/atoms";
import {
  AddUserPlan,
  UpdateUserPlan,
} from "store/middlewares/dataManagement/UserPlan";

function ModalEditUserPlan({ id }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState(false);
  const { userPlan } = useSelector((state) => state.userPlan);

  const handleSubmit = (values) => {
    const UpdatedValue = {
      ...values,
      price: Number(values.price),
      features: values.features.map((f) => ({
        ...f,
        value: f.value.toString(),
      })),
      onSuccess: () => formik.setSubmitting(false),
      onError: () => setFormError(true),
    };

    if (id) {
      dispatch(UpdateUserPlan({ id: id, ...UpdatedValue }));
    } else {
      dispatch(AddUserPlan(UpdatedValue));
    }
  };

  const planData = userPlan?.data?.filter((item) => item.id === id);

  const FeatureValue = (feature_type) => {
    const tempObj = planData?.[0].features.filter(
      (item) => item.feature_type === feature_type,
    );
    const value = tempObj?.[0]?.value;
    return value;
  };

  const formik = useFormik({
    initialValues: {
      name: id ? planData?.[0]?.name : "",
      description: id ? planData?.[0]?.description : "",
      price: id ? planData?.[0]?.price : "",
      type: id ? planData?.[0]?.type : "",
      isRecommend: id ? planData?.[0]?.isRecommend : true,
      features: [
        {
          title: "Storage",
          featureType: "storage_total_gb",
          value: id ? FeatureValue("storage_total_gb") : "",
          unit: "GB",
        },
        {
          title: "Mail Storage",
          featureType: "storage_mail_gb",
          value: id ? FeatureValue("storage_mail_gb") : "",
          unit: "GB",
        },
        {
          title: "Drive Storage",
          featureType: "storage_drive_gb",
          value: id ? FeatureValue("storage_drive_gb") : "",
          unit: "GB",
        },
        {
          title: "Email Addresses",
          featureType: "email_addresses",
          value: id ? FeatureValue("email_addresses") : "",
          unit: "",
        },
        {
          title: "Folders & Labels",
          featureType: "folders_labels",
          value: id ? FeatureValue("folders_labels") : "",
          unit: "count",
        },
        {
          title: "Message Limit",
          featureType: "messages_per_day",
          value: id ? FeatureValue("messages_per_day") : "",
          unit: "per day",
        },
        {
          title: "Support",
          featureType: "support_level",
          value: id ? FeatureValue("support_level") : "",
          unit: "",
        },
      ],
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      description: Yup.string().required("Description is required"),
      price: Yup.string().required("Price is required"),
      type: Yup.string().required("Type is required"),
      isRecommend: Yup.boolean(),

      features: Yup.array().of(
        Yup.object().shape({
          title: Yup.string().required("Feature title is required"),
          featureType: Yup.string().required("Feature type is required"),
          value: Yup.string().required("Value is required"),
          unit: Yup.string(),
        }),
      ),
    }),
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <div className="grid gap-5 lg:gap-7.5 mx-auto">
          <Card className="min-w-full">
            <CardHeader>
              <CardTitle>User Plan</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5">
              <div className="flex items-baseline w-full gap-10">
                <div className="flex items-baseline w-full flex-wrap lg:flex-nowrap gap-2.5">
                  <Label className="flex w-full max-w-32">Plan Name</Label>
                  <Input
                    error={formik.errors.name && formik.touched.name}
                    errorMsg={formik.errors.name}
                    value={formik.values.name}
                    type="text"
                    name="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isTextOnly={true}
                    placeholder="Enter plan name"
                    className={"!py-0 w-full "}
                  />
                </div>
                <div className="flex items-baseline w-full flex-wrap lg:flex-nowrap gap-2.5">
                  <Label className="flex w-full max-w-32 ">Description</Label>
                  <Input
                    error={
                      formik.errors.description && formik.touched.description
                    }
                    errorMsg={formik.errors.description}
                    value={formik.values.description}
                    type="text"
                    name="description"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter description"
                    className={"!py-0 w-full"}
                  />
                </div>
              </div>
              <div className="flex items-baseline w-full gap-10">
                <div className="flex items-baseline w-full flex-wrap lg:flex-nowrap gap-2.5 ">
                  <Label className="flex w-full max-w-32 ">Price</Label>
                  <Input
                    error={formik.errors.price && formik.touched.price}
                    errorMsg={formik.errors.price}
                    value={formik.values.price}
                    isNumeric={true}
                    name="price"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter price"
                    className={"!py-0 w-full"}
                  />
                </div>
                <div className="flex items-baseline w-full flex-wrap lg:flex-nowrap gap-2.5">
                  <Label className="flex w-full max-w-32 ">Type</Label>
                  <CustomSelect
                    error={formik.errors.type && formik.touched.type}
                    errorMsg={formik.errors.type}
                    val={formik.values.type}
                    name="type"
                    options={["monthly", "yearly"].map((item) => {
                      return {
                        label: item,
                        value: item,
                      };
                    })}
                    onChange={(selectedOption) => {
                      formik.setFieldValue(
                        "type",
                        selectedOption ? selectedOption : "",
                      );
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Enter type"
                    className={"!py-0 w-full"}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <VisiblitySwitch
                  checked={formik.values.isRecommend}
                  onChange={() =>
                    formik.setFieldValue(
                      "isRecommend",
                      !formik.values.isRecommend,
                    )
                  }
                />
                <Label>Recommended Plan?</Label>
              </div>

              <FieldArray
                name="features"
                render={() => (
                  <>
                    <Label className="font-semibold text-base mt-5">
                      Plan Features
                    </Label>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {formik.values.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex flex-wrap lg:flex-nowrap gap-5 items-baseline"
                        >
                          <div className="flex items-baseline w-full flex-wrap lg:flex-nowrap gap-2.5">
                            <Label className="flex w-full max-w-32 ">
                              {feature.title}
                            </Label>
                            <Input
                              name={`features[${index}].value`}
                              value={formik.values.features[index].value}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder={`Enter ${feature.title}`}
                              error={
                                getIn(
                                  formik.touched,
                                  `features[${index}].value`,
                                ) &&
                                getIn(formik.errors, `features[${index}].value`)
                              }
                              errorMsg={getIn(
                                formik.errors,
                                `features[${index}].value`,
                              )}
                              className={"!py-0 w-full"}
                            />
                          </div>
                          <div className="text-xs text-muted-foreground w-[80px] text-left pt-4">
                            {feature.unit}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              />
              <div className="flex justify-end">
                <SubmitLoaderButton
                  btnText={id ? "Update" : "Add"}
                  isDisable={formik.isSubmitting && !formError}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </FormikProvider>
  );
}

export default ModalEditUserPlan;
